import React, { useContext, useEffect } from 'react';
import Helmet from 'react-helmet';
import Layout from '@/components/layout';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import { PADDING } from '@latitude/core/utils/constants';
import { PageContext } from '@/context/PageContext';
import GoMicrositeHeader from './_go-microsite-header-rebrand';
import { NotificationBanner } from '@latitude/banner';
import FeatureCardList from '@/components/FeatureCardListRebrand/FeatureCardListRebrand';
import { CARD_CODE, CARD_DATA } from '@/data/pages/credit-cards/card-offer';
import { AnalyticsContext } from 'latitude-analytics';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import FeatureGridV1 from '@/components/FeatureGridV1/FeatureGridV1';
import LabRatesAndFeesColumn from '@/components/LabRatesAndFeesColumnRebrand';
import StickyNavigation from '@/components/StickyNavigationRebrand/StickyNavigation';
import HowToApply from '@/components/HowToApplyRebrand/HowToApplyRebrand';
import PromoBanner from '@/components/PromoBannerRebrand/PromoBannerRebrand';
import PaymentOption from './_payment-options';
import SvgInline from '@latitude/svg-inline';
import { Link } from '@latitude/link';
import IFExplainer from '@/components/lab-components/IFExplainerRebrand';
import { getIFExplainerDataFromContentful } from '@/components/lab-components/IFExplainerRebrand/contentfulIFExplainer';
import PageData from '@/data/pages/credit-cards/gomastercard.json';
import { getFeatureGridV1DataFromContentful } from '@/components/FeatureGridV1/contentfulFeatureGridV1';
import { getFeatureCardListRebrandDataFromContentful } from '@/components/FeatureCardListRebrand/contentfulFeatureCardListRebrand';
import OfferSelector from '@/components/Offers/OfferSelector/OfferSelector';
import { getOfferSelectorDataFromContentful } from '@/components/Offers/OfferSelector/contentfulOfferSelector';

const PAGE_NAVIGATION = [
  {
    anchor: 'benefits',
    label: 'Benefits'
  },
  {
    anchor: 'rates',
    label: 'Rates & Fees'
  },
  {
    anchor: 'payment-options',
    label: 'Payment options'
  },
  {
    anchor: 'how-to-apply',
    label: 'How to apply'
  }
];

const FEATURE_GRID_DATA = {
  features: [
    {
      icon: <SvgInline name="icon-seal-percent-rebrand" />,
      headingText: '0% interest offers from 6 months',
      paragraphText: (
        <>
          We offer a range of Interest Free Payment Plans for eligible purchases
          at Harvey Norman<sup>®</sup>, Domayne<sup>®</sup>, and Joyce Mayne
          <sup>®</sup>, to match the way you want to pay.
        </>
      )
    },
    {
      icon: <SvgInline name="icon-calender-dots-rebrand" />,
      headingText: '0% on $250+ purchases',
      paragraphText: (
        <>
          Enjoy 0% interest for 6 months on general purchases of $250 or more.
          Select your purchases in the Latitude app within 30 days.
        </>
      )
    },
    {
      icon: <SvgInline name="icon-basket-rebrand" />,
      headingText: 'The perfect card, everyday',
      paragraphText: (
        <>
          The Latitude GO Mastercard credit card offers up to 55 days Interest
          Free<sup>1</sup> on general purchases, so it’s the perfect go-to card
          for your everyday spending too.
        </>
      )
    },
    {
      icon: <SvgInline name="icon-calender-star-rebrand" />,
      headingText: 'Stay on track with your spending',
      paragraphText: (
        <>
          With the{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/mobile-app/"
            trackEventData={{
              label: 'latitude-app',
              location: 'stay-on-track-with-your-spending'
            }}
          >
            Latitude App
          </Link>{' '}
          , it's never been easier to manage your payments and stay on top of
          your due dates.
        </>
      )
    }
  ],
  moreFeatureTitle: 'View all benefits',

  moreFeatures: [
    {
      title: `Ready, Set, Tap`,
      content: (
        <>
          Introducing our{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/digital-wallets/apple-pay/"
            trackEventData={{
              label: 'instant-cardless-payment',
              location: 'ready-set-tap'
            }}
          >
            instant cardless payment
          </Link>{' '}
          feature where you can apply, get approval, and start shopping from
          your phone in minutes.
        </>
      )
    },
    {
      title: `Save with Expedia and Wotif`,
      content: (
        <>
          Enjoy awesome discounts on selected hotels all day, every day,
          year-round with our travel partners.{' '}
          <Link
            noStyle
            style={{ textDecoration: 'underline' }}
            href="/travelpartners/"
            trackEventData={{
              label: 'book-now',
              location: 'save-with-expedia-wotif'
            }}
          >
            Book now
          </Link>{' '}
        </>
      )
    }
  ]
};

const HOW_TO_APPLY_DATA = {
  id: 'how-to-apply',
  howToApply: {
    sectionTitle: 'How To apply',
    items: [
      {
        title: 'Apply online',
        description: 'You can check your eligibility and apply in 10 minutes.'
      },
      {
        title: 'Wait a minute',
        description:
          '60 seconds is all it takes for us to respond to your application.'
      },
      {
        title: 'Ready, set, shop',
        description:
          'Simply activate your card when it arrives to start shopping.'
      }
    ]
  },
  eligibility: {
    sectionTitle: 'Eligibility',
    items: [
      {
        title: 'You must:',
        list: [
          'Be 18 years or over',
          'A resident of Australia or a temporary resident who legally has the right to work in Australia (and intend to stay) for at least the next 12 months.'
        ]
      },
      {
        title: 'Able to supply',
        list: [
          "A copy of your driver's licence, passport or proof of age card",
          'Details of your income, assets and debts',
          "Employer's name and contact details"
        ]
      }
    ]
  }
};

const OFFER_SELECTOR_DATA = {
  heading: 'Which offer would you like to apply to?',
  offers: [
    {
      id: 'bt-24months',
      title: 'Apply for 0% Balance Transfer for 26 months offer',
      subtitle: '',
      content:
        '3% Balance Transfer fee and eligibility criteria apply. New customers only, must be approved by 26 Feb 2027. Balance Transfer must be submitted by 31 March 2027. T&Cs, monthly credit card fee (currently $11.95) and other charges may apply. Interest rate reverts to the cash advance rate after the promotional period, currently 29.99%. A Balance Transfer cannot be made from a Latitude issued credit card.',
      productId: '8F5UH373',
      ctaText: 'Apply now',
      ctaURL: 'https://cards.latitudefinancial.com/go'
    },
    {
      id: 'no-bt-offer',
      title: 'Apply for the $400 Harvey Norman® eGift Card offer',
      subtitle: '',
      content:
        'Shop smart and get a $400 Harvey Norman eGift Card when you spend $1,500 per month on general purchases, including Interest Free plans, for the first 4 months after approval with your Latitude GO Mastercard credit card. T&Cs, monthly credit card fee (currently $11.95) and other charges may apply. New customers only, approved by 29 January 2027.  ',
      ctaText: 'Apply now',
      ctaURL: 'https://cards.latitudefinancial.com/go'
    }
  ]
};

const IF_EXPLAINER_DATA = {
  title: 'Pay over time with Latitude Interest Free payment plans',
  description:
    'There are several ways to pay when you choose a long-term Interest Free payment plan at 100s of retailer partners. Flexible plan lengths might differ by store.',
  tiles: [
    {
      id: 1,
      tagText: 'Always Equal',
      title: 'Equal Monthly plan',
      description: [
        'Keep it simple and pay the same amount every month.',
        'Total plan amount is paid in the agreed number of monthly payments. ',
        'Gives you flexibility to pay out early without any penalty.'
      ]
    },
    {
      id: 2,
      tagText: 'Pay extra anytime',
      title: 'Minimum Monthly plan',
      description: [
        'Available provided you pay your credit card minimum payment each month.',
        'Gives you flexibility to add extra when possible.',
        'Paying this way may not clear the plan within your plan period.',
        'Interest applies on any outstanding balance at the end of your plan period.'
      ]
    },
    {
      id: 3,
      tagText: 'Pay anytime',
      title: 'Deferred Payment plan',
      description: [
        'No need to pay anything upfront.',
        'Make payments at any time it suits you during plan period.',
        'Interest applies on any outstanding balance at the end of your plan period.'
      ]
    }
  ]
};
// Card data array
const cardData = CARD_DATA.filter(card => card.code !== CARD_CODE.GO);


const schemaData = {
  '@context': 'https://schema.org',
  contactlessPayment: 'true',
  '@type': 'CreditCard',
  annualPercentageRate: '27.99',
  image:
    'https://assets.latitudefinancial.com/legals/key-fact-sheet/gomc/latitude-go-mastercard.webp',
  url: 'https://www.latitudefinancial.com.au/credit-cards/gomastercard/',
  category: 'Latitude Interest Free Credit Cards',
  brand: { '@type': 'Brand', name: 'Latitude Financial Services' },
  amount: { '@type': 'MonetaryAmount', minValue: '1000', currency: 'AUD' },
  name: 'Latitude GO Mastercard',
  description:
    'Get 6 months interest free on your everyday purchases of $250 or more and shop with interest free payment plans at Harvey Norman, Domayne and Joyce Mayne.',
  requiredCollateral: '$1000 minimum credit limit'
};

function GoMastercardHomePage(props) {
  const gatsbyTealiumEnv = process.env.GATSBY_TEALIUM_ENV || 'unknown';
  const dynamicYieldKey = gatsbyTealiumEnv === 'prod' ? '8790471' : '8790470';

  /** Contentful Data */
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || {};
  const featureGridData =
    getFeatureGridV1DataFromContentful(state?.featureSliderData?.[0]) ||
    FEATURE_GRID_DATA;
  const featureCardListData = getFeatureCardListRebrandDataFromContentful(
    state?.featureCardListRebrandData?.[0]
  ) || { cardList: cardData };
  const ifExplainerData =
    getIFExplainerDataFromContentful(state?.ifExplainerCollectionData?.[0]) ||
    IF_EXPLAINER_DATA;
  /** - END - */

  const [analytics] = useContext(AnalyticsContext);

    const offerSelectorData =
      getOfferSelectorDataFromContentful(state?.offerSelectorData?.[0]) ||
      OFFER_SELECTOR_DATA;

  useEffect(() => {
    const sourceCode = localStorage.getItem('source_code') || 'IDAAA';
    // adding pageData to analytics
    analytics.pageData = {
      page_pageTitle: 'gomastercard',
      page_pageType: 'product-details-page',
      page_siteSection: 'cards',
      page_siteSubsection: 'gomastercard',
      product_productid: ['CCAUGOM'],
      application_sourcecode: sourceCode || 'IDAAA'
    };
  }, []);

  return (
    <Layout location={props.location} hasSecNav>
      <main>
        <Helmet>
          <link
            rel="canonical"
            href="https://www.latitudefinancial.com.au/credit-cards/gomastercard/"
          />
          <script type="application/ld+json">
            {JSON.stringify(schemaData)}
          </script>
          <title>
            GO Mastercard Credit Card | Shop With 0% Interest Payment Plans
          </title>
          <meta
            name="description"
            content="Take control of your spending with Latitude GO Mastercard. Opt in within 30 days to enjoy 6 months Interest Free on everyday purchases over $250. Apply online"
          />

          <script type="text/javascript">
            {`
              window.DY = window.DY || {};
              DY.recommendationContext = { type: "PRODUCT", data: ['gomastercard'] };
            `}
          </script>
 
        </Helmet>
        <GoMicrositeHeader data={state?.secondaryNavData} />
        {!state?.alertData && (
          <NotificationBanner siteName="latitudefinancial-com-au" />
        )}
        <HeroV2
          {...(state?.heroBannerData?.[0] ||
            PageData?.contentful?.heroBannerData?.[0])}
        />

        <StickyNavigation
          items={PAGE_NAVIGATION}
          enableAnchorUpdate={true}
          trackId="sticky-get-started"
          stickyOffset={146}
        />

                {/* <OfferSelector
          trackingId="lowrate"
          id="limited-time-offer-selector"
          {...offerSelectorData}
        /> */}


        <FeatureGridV1
          id="benefits"
          trackingId="lowrate"
          moreFeatureTitle={FEATURE_GRID_DATA.moreFeatureTitle}
          {...featureGridData}
        />
        {state?.promoBannerData?.[0] && (
          <PromoBanner {...state?.promoBannerData?.[0]} />
        )}

        {state?.ratesAndFeesData?.[0] && (
          <LabRatesAndFeesColumn isRate={true} {...state.ratesAndFeesData[0]} />
        )}

        <PaymentOption />
        {state?.promoBannerData?.[1] && (
          <PromoBanner {...state?.promoBannerData?.[1]} />
        )}

        <HowToApply data={HOW_TO_APPLY_DATA} />

        <FeatureCardList {...featureCardListData} />

        <ImportantInformation
          id="important-Info"
          css={`
            overflow-x: hidden;
            .row {
              @media (min-width: 1600px) {
                padding-left: ${PADDING.P32};
                padding-right: ${PADDING.P32};
              }
            }
          `}
          data={{
            content: {
              importantInformationSectionOne: [
                '# To take advantage of up to 55 days Interest Free on everyday credit card purchases, you need to pay the full closing balance on each statement of account by the applicable due date.',
                "<sup id='note-1'>1</sup> Latitude 28&deg; Global Platinum Mastercard cardholders will be eligible for a $10 Latitude Reward every statement period which is redeemable for an e-gift card from participating retailers when they spend $1,000 or more on eligible transactions using their credit card within the statement period. Any purchases made by an additional cardholder will contribute towards the primary cardholder's spend threshold. Eligible transactions are based on the date posted and do not include cash advances, cash equivalent transactions, card fees or charges, credits, refunds, reimbursements, Shopper's Protection premiums, interest and balance transfers into an account. The account must be open and not in default of the credit contract when the Latitude Rewards is issued. The Latitude Rewards will be issued by EonX Services Pty Ltd and will be available for redemption via the Latitude App or Latitude Service Centre within 30 days from the end of the statement period. The primary cardholder has up to 18 months to redeem from the date of issue. <a href='https://assets.latitudefinancial.com/legals/conditions-of-use/28degrees-au/cou.pdf' target='_blank'>View full terms and conditions.</a>'",
                '<sup id="note-2">2</sup> Purchase Protection Insurance is underwritten and issued by AIG Australia Limited ABN 93 004 727 753, AFSL 381 686, Level 19, 2 Park Street, Sydney NSW 2000 (“AIG”) under a group policy of insurance issued to Mastercard. In arranging the insurance Mastercard acts a group purchasing body under ASIC Corporations (Group Purchasing Bodies) Instrument 2018/751. Eligible persons who can access the complimentary Purchase Protection Insurance are the current holders of the relevant Latitude 28° Global Platinum Mastercard which entitles them to these purchase protection insurance benefits. It is important you read your Latitude 28° Global Platinum Mastercard Purchase Protection Insurance Terms and Conditions. Eligible persons can claim as third-party beneficiaries by virtue of the operation of s48 of the Insurance Contracts Act 1984. Conditions and Exclusions apply to this insurance coverage as set out in the group policy and any insurance document provided to the eligible persons and which both may be amended from time to time. Neither Latitude Finance Australia nor Mastercard guarantees this insurance and do not hold an Australian Financial Services Licence. <a targe="_blank" href="https://assets.latitudefinancial.com/legals/28degreescard/purchase-protection-insurance-termsconds.pdf">View full terms and conditions</a>.',
                '<sup id="note-3">3</sup> E-Commerce Purchase Protection Insurance is underwritten and issued by AIG Australia Limited ABN 93 004 727 753, AFSL 381 686, Level 19, 2 Park Street, Sydney NSW 2000 (“AIG”) under a group policy of insurance issued to Mastercard. In arranging the insurance Mastercard acts a group purchasing body under ASIC Corporations (Group Purchasing Bodies) Instrument 2018/751. Eligible persons who can access the complimentary E-Commerce Purchase Protection Insurance are the current holder of the relevant Latitude 28° Global Platinum Mastercard which entitles them to these e-commerce purchase protection insurance benefits. It is important you read your Latitude 28° Global Platinum Mastercard E-Commerce Purchase Protection Insurance Terms and Conditions. Eligible persons can claim as third-party beneficiaries by virtue of the operation of s48 of the Insurance Contracts Act 1984. Conditions and Exclusions apply to this insurance coverage as set out in the group policy and any insurance document provided to the eligible persons and which both may be amended from time to time. Neither Latitude Finance Australia nor Mastercard guarantees this insurance and do not hold an Australian Financial Services Licence. <a targe="_blank" href="https://assets.latitudefinancial.com/legals/28degreescard/ecommerce-protection-insurance-termscond.pdf">View full terms and conditions</a>.',
                'From 01/03/26 to 30/06/26, customers who purchase products from a Harvey Norman®, Domayne® or Joyce',
                'Mayne® franchisee using the Latitude 60 Months Interest Free offer (as described above in #1) with a purchase',
                'price of $1000 or more (<b>Eligible Purchase</b>) will be eligible to receive one Harvey Norman®, Domayne® or Joyce'
              ],
              importantInformationSectionTwo: [
                '<sup id="note-4">4</sup> The Flight Delay Pass gives free access to select airport lounges, alternatively cardholders can use their Flight Delay Pass to offset a dollar amount off the total bill at select dining options. This will vary based on location and affiliated LoungeKey Partners at the time. Please note that not all airports offer Flight Delay Pass and the selected airports may be restricted to certain terminals. Access to and use of the Flight Delay Pass is subject to the Terms of Use which are subject to change. Further details of the list of lounges that cardholders are eligible to visit and the specific conditions applicable to each lounge access (Conditions of Use) may also be accessed in the Terms of Use. Participating lounges and their Conditions of Use are subject to change.',
                'Mastercard and the circles design are registered trademarks of Mastercard International Incorporated.',
                'Apple, the Apple logo, Apple Pay, Apple Watch, Face ID, iPad, iPhone, Safari, and Touch ID are trademarks of Apple Inc., and App Store is a service mark of Apple Inc., registered in the U.S. and other countries.',
                'Samsung and Samsung Pay are trademarks or registered trademarks of Samsung Electronics Co., Ltd.',
                'Garmin, the Garmin logo, and the Garmin delta are trademarks of Garmin Ltd. or its subsidiaries and are registered in one or more countries, including the U.S. Garmin Pay is a trademark of Garmin Ltd. or its subsidiaries.',
                'Google Pay, Android, the Google Pay Logo, Google Play and the Google Play logo are trademarks of Google LLC.',
                '&reg; Registered to BPAY Pty Ltd ABN 69 079 137 518',
                "<sup id='note-5'>5</sup>$300 Latitude Reward is available to new and approved applicants by 28 May 2025 who apply for a Latitude 28° Global Platinum Mastercard and is limited to one offer per applicant. To qualify, you must be approved and fulfil on the terms of the offer by spending $1,000 or more on eligible transactions each statement period for 3 months from the date of your approval. Any purchases made by an additional cardholder will contribute towards the primary cardholder's spend threshold. Eligible transactions are based on the date posted and do not include cash advances, cash equivalent transactions, card fees or charges, credits, refunds, reimbursements, Shopper's Protection premiums, interest and balance transfers into an account. The account must be open and not in default or the credit contract when the redeemable amount is issued. The Latitude Rewards will be issued by EonX Services Pty Ltd and will be available for redemption via the Latitude App or Latitude Service Centre within 30 days of meeting the spend criteria. The primary cardholder has up to 18 months to redeem from the date of issue. Latitude may vary, extend or withdraw this offer at any time without notice.",
                "<sup id='note-6'>6</sup>$0 Monthly Card fees for your first 12 months is available to new and approved applicants by 28 May 2025 who apply for a Latitude 28° Global Platinum Mastercard and is limited to one offer per applicant. After the first year, the monthly card fee reverts to $8 per month (subject to change) and will be debited to your card account on your first statement following your 12-month anniversary and monthly thereafter. Latitude may vary, extend or withdraw this offer at any time without notice.​"
              ]
            }
          }}
          sectionOneColummClasses="col-12 col-lg-6"
          sectionTwoColummClasses="col-12 col-lg-6"
          {...{
            ...state?.importantInfoData?.[0],
            sectionOneColummClasses: 'col-12 col-lg-6',
            sectionTwoColummClasses: 'col-12 col-lg-6'
          }}
        />
      </main>
    </Layout>
  );
}

export default GoMastercardHomePage;
